// ./src/components/features/DisplayConfigurations/ScheduleTypeSelector/ScheduleTypeSelector.tsx
import React, { useEffect } from "react";
import { Select, SelectProps } from "@mantine/core";

const SCHEDULE_TYPE_OPTIONS = [
  { value: "current_day", label: "Current Day", disabled: false },
  { value: "current_week", label: "Current Week", disabled: false },
  { value: "current_month", label: "Current Month", disabled: false },
  { value: "next_day", label: "Next Day", disabled: false },
  { value: "next_week", label: "Next Week", disabled: false },
  { value: "next_month", label: "Next Month", disabled: false },
  { value: "upcoming", label: "Upcoming", disabled: false },
];

export interface ScheduleTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current status value (100 for enabled, 0 for disabled) */
  value: string | null | undefined;

  /** Callback when status changes */
  onChange: (value: string | null | undefined) => void;

  required?: boolean;

  /** Whether to show icons in the dropdown */
  withIcons?: boolean;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;
}

export const ScheduleTypeSelector: React.FC<ScheduleTypeSelectorProps> = ({
  value,
  onChange,
  required,
  withIcons = false,
  withDescriptions = false,
  placeholder = "Select display type",
  ...selectProps
}) => {
  // Convert numeric value to string for Select component
  const stringValue = value !== null ? String(value) : null;

  // Prepare select data with optional descriptions
  const selectData = SCHEDULE_TYPE_OPTIONS.map((option: any) => {
    let label: string = option.label;

    if (withDescriptions) {
      label = `${option.label} - ${option.description}`;
    }

    return {
      value: option.value,
      label,
      disabled: option.disabled,
    };
  });

  // Auto-select first display type if required and no value is set
  useEffect(() => {
    if (required && !value && onChange) {
      const firstOption = SCHEDULE_TYPE_OPTIONS[0];
      if (firstOption && !firstOption.disabled) {
        onChange(firstOption.value);
      }
    }
  }, [required, value, onChange]);

  // Handle change and convert back to number
  const handleChange = (stringValue: string | null) => {
    if (stringValue === null) {
      onChange(null);
    } else {
      onChange(stringValue);
    }
  };

  return (
    <Select
      {...selectProps}
      data={selectData}
      value={stringValue}
      onChange={handleChange}
      placeholder={placeholder}
      clearable={!required}
      required={required}
      allowDeselect={!required}
      withAsterisk={required}
      size="md"
    />
  );
};

// Export status options for use in other components
export { SCHEDULE_TYPE_OPTIONS };
